import type { Route, Routes } from '@angular/router';
import { pendingChangesGuard } from '../../shared/guards/pending-changes.guard';
import { AssetChargeDetailPageComponent } from './pages/asset-charges/asset-charge-detail-page.component';
import { AssetChargesPageComponent } from './pages/asset-charges/asset-charges-page.component';
import { AssetDetailPageComponent } from './pages/assets/asset-detail-page.component';
import { AssetEditorPageComponent } from './pages/assets/asset-editor-page.component';
import { AssetsPageComponent } from './pages/assets/assets-page.component';
import { OrganizationSettingsPageComponent } from './pages/organization-settings/organization-settings-page.component';
import { PERFORMANCE_ROUTE } from './pages/performance/performance.routes';
import { RecruitmentBoardStateStore } from './pages/recruitment/shared/state/recruitment-board-state.store';
import { RecruitmentContextStore } from './pages/recruitment/shared/state/recruitment-context.store';
import { RecruitmentInboxBadgeStore } from './pages/recruitment/shared/state/recruitment-inbox-badge.store';
import { RecruitmentReferenceStore } from './pages/recruitment/shared/state/recruitment-reference.store';
import { PROJECT_WORKSPACE_ROUTE, TASKS_ROUTE } from './pages/tasks/tasks.routes';
import { TasksContextStore } from './pages/tasks/shared/state/tasks-context.store';
import { TasksReferenceStore } from './pages/tasks/shared/state/tasks-reference.store';
import { BENEFITS_ROUTE } from './pages/benefits/benefits.routes';
import { BONUSES_ROUTE } from './pages/bonuses/bonuses.routes';
import { COMMUNICATIONS_ROUTE } from './pages/communications/communications.routes';
import { AI_AGENTS_ROUTE } from './pages/ai-agents/ai-agents.routes';

const dashboardLoader = () => import('./pages/dashboard/dashboard-page.component').then((m) => m.DashboardPageComponent);
const profileLoader = () => import('./pages/profile/profile-page.component').then((m) => m.ProfilePageComponent);
const directorySyncRunLoader = () =>
  import('./pages/directory-sync-run/directory-sync-run-page.component').then((m) => m.DirectorySyncRunPageComponent);
const organizationChartLoader = () =>
  import('./pages/organization-chart-page/organization-chart-page.component').then((m) => m.OrganizationChartPageComponent);
const usersListLoader = () => import('./pages/users-list-page/users-list-page.component').then((m) => m.UsersListPageComponent);
const userDetailLoader = () => import('./pages/user-detail-page/user-detail-page.component').then((m) => m.UserDetailPageComponent);
const userEditLoader = () => import('./pages/user-edit-page/user-edit-page.component').then((m) => m.UserEditPageComponent);
const employeeDocumentsLoader = () =>
  import('./pages/employee-documents/employee-documents-page.component').then((m) => m.EmployeeDocumentsPageComponent);
const professionalProfileLoader = () =>
  import('./pages/professional-profile/professional-profile-page.component').then((m) => m.ProfessionalProfilePageComponent);
const employeeProfilesLoader = () =>
  import('./pages/employee-profiles/employee-profiles-page.component').then((m) => m.EmployeeProfilesPageComponent);
const skillsLoader = () => import('./pages/skills/skills-page.component').then((m) => m.SkillsPageComponent);
const branchesListLoader = () =>
  import('./pages/branches/branches-list-page/branches-list-page.component').then((m) => m.BranchesListPageComponent);
const branchEditorLoader = () =>
  import('./pages/branches/branch-editor-page/branch-editor-page.component').then((m) => m.BranchEditorPageComponent);
const branchDetailLoader = () =>
  import('./pages/branches/branch-detail-page/branch-detail-page.component').then((m) => m.BranchDetailPageComponent);
const departmentsListLoader = () =>
  import('./pages/departments/departments-list-page/departments-list-page.component').then((m) => m.DepartmentsListPageComponent);
const departmentDetailLoader = () =>
  import('./pages/departments/department-detail-page/department-detail-page.component').then((m) => m.DepartmentDetailPageComponent);
const departmentEditorLoader = () =>
  import('./pages/departments/department-editor-page/department-editor-page.component').then((m) => m.DepartmentEditorPageComponent);
const positionsListLoader = () =>
  import('./pages/positions/positions-list-page/positions-list-page.component').then((m) => m.PositionsListPageComponent);
const positionDetailLoader = () =>
  import('./pages/positions/position-detail-page/position-detail-page.component').then((m) => m.PositionDetailPageComponent);
const positionEditorLoader = () =>
  import('./pages/positions/position-editor-page/position-editor-page.component').then((m) => m.PositionEditorPageComponent);
const rolesListLoader = () => import('./pages/roles/roles-list-page/roles-list-page.component').then((m) => m.RolesListPageComponent);
const roleDetailLoader = () => import('./pages/roles/role-detail-page/role-detail-page.component').then((m) => m.RoleDetailPageComponent);
const roleEditorLoader = () => import('./pages/roles/role-editor-page/role-editor-page.component').then((m) => m.RoleEditorPageComponent);
const holidaysListLoader = () =>
  import('./pages/holidays/holidays-list-page/holidays-list-page.component').then((m) => m.HolidaysListPageComponent);
const holidayEditorLoader = () =>
  import('./pages/holidays/holiday-editor-page/holiday-editor-page.component').then((m) => m.HolidayEditorPageComponent);
const holidayDetailLoader = () =>
  import('./pages/holidays/holiday-detail-page/holiday-detail-page.component').then((m) => m.HolidayDetailPageComponent);
const shiftsListLoader = () =>
  import('./pages/shifts/shifts-list-page/shifts-list-page.component').then((m) => m.ShiftsListPageComponent);
const shiftDetailLoader = () =>
  import('./pages/shifts/shift-detail-page/shift-detail-page.component').then((m) => m.ShiftDetailPageComponent);
const shiftEditorLoader = () =>
  import('./pages/shifts/shift-editor-page/shift-editor-page.component').then((m) => m.ShiftEditorPageComponent);
const attendanceListLoader = () =>
  import('./pages/attendance/attendance-list-page/attendance-list-page.component').then((m) => m.AttendanceListPageComponent);
const manualAttendanceLoader = () =>
  import('./pages/attendance/manual-attendance-page/manual-attendance-page.component').then((m) => m.ManualAttendancePageComponent);
const fieldOperationsLoader = () =>
  import('./pages/field-operations/field-operations-page.component').then((m) => m.FieldOperationsPageComponent);
const deductionsListLoader = () =>
  import('./pages/deductions/deductions-list-page/deductions-list-page.component').then((m) => m.DeductionsListPageComponent);
const deductionEditorLoader = () =>
  import('./pages/deductions/deduction-editor-page/deduction-editor-page.component').then((m) => m.DeductionEditorPageComponent);
const deductionDetailLoader = () =>
  import('./pages/deductions/deduction-detail-page/deduction-detail-page.component').then((m) => m.DeductionDetailPageComponent);
const substitutesListLoader = () =>
  import('./pages/substitutes/substitutes-list-page/substitutes-list-page.component').then((m) => m.SubstitutesListPageComponent);
const substituteEditorLoader = () =>
  import('./pages/substitutes/substitute-editor-page/substitute-editor-page.component').then((m) => m.SubstituteEditorPageComponent);
const substituteDetailLoader = () =>
  import('./pages/substitutes/substitute-detail-page/substitute-detail-page.component').then((m) => m.SubstituteDetailPageComponent);
const userBudgetListLoader = () =>
  import('./pages/user-budget/user-budget-list-page/user-budget-list-page.component').then((m) => m.UserBudgetListPageComponent);
const userBudgetEditorLoader = () =>
  import('./pages/user-budget/user-budget-editor-page/user-budget-editor-page.component').then((m) => m.UserBudgetEditorPageComponent);
const userBudgetDetailLoader = () =>
  import('./pages/user-budget/user-budget-detail-page/user-budget-detail-page.component').then((m) => m.UserBudgetDetailPageComponent);
const leavesListLoader = () => import('./pages/leaves/leaves-list-page/leaves-list-page.component').then((m) => m.LeavesListPageComponent);
const leaveEditorLoader = () => import('./pages/leaves/leave-editor-page/leave-editor-page.component').then((m) => m.LeaveEditorPageComponent);
const leaveDetailLoader = () => import('./pages/leaves/leave-detail-page/leave-detail-page.component').then((m) => m.LeaveDetailPageComponent);
const userLeaveBalancesLoader = () =>
  import('./pages/leaves/user-leave-balances-page/user-leave-balances-page.component').then((m) => m.UserLeaveBalancesPageComponent);
const userLeaveBalanceDetailLoader = () =>
  import('./pages/leaves/user-leave-balance-detail-page/user-leave-balance-detail-page.component').then(
    (m) => m.UserLeaveBalanceDetailPageComponent,
  );
const hourlyLeavePermissionsLoader = () =>
  import('./pages/hourly-leave-permissions/hourly-leave-permissions-page.component').then(
    (m) => m.HourlyLeavePermissionsPageComponent,
  );
const policiesListLoader = () => import('./pages/policies/policies-list-page/policies-list-page.component').then((m) => m.PoliciesListPageComponent);
const policyEditorLoader = () => import('./pages/policies/policy-editor-page/policy-editor-page.component').then((m) => m.PolicyEditorPageComponent);
const policyDetailLoader = () => import('./pages/policies/policy-detail-page/policy-detail-page.component').then((m) => m.PolicyDetailPageComponent);
const policyHistoryLoader = () => import('./pages/policies/policy-history-page/policy-history-page.component').then((m) => m.PolicyHistoryPageComponent);
const loanSetupListLoader = () =>
  import('./pages/loan-setup/loan-setup-list-page/loan-setup-list-page.component').then((m) => m.LoanSetupListPageComponent);
const loanSetupEditorLoader = () =>
  import('./pages/loan-setup/loan-setup-editor-page/loan-setup-editor-page.component').then((m) => m.LoanSetupEditorPageComponent);
const loanSetupDetailLoader = () =>
  import('./pages/loan-setup/loan-setup-detail-page/loan-setup-detail-page.component').then((m) => m.LoanSetupDetailPageComponent);
const payrollsListLoader = () => import('./pages/payrolls/payrolls-list-page/payrolls-list-page.component').then((m) => m.PayrollsListPageComponent);
const payrollDetailLoader = () => import('./pages/payrolls/payroll-detail-page/payroll-detail-page.component').then((m) => m.PayrollDetailPageComponent);
const requestsListLoader = () =>
  import('./pages/requests/requests-list-page/requests-list-page.component').then((m) => m.RequestsListPageComponent);
const userCreateLoader = () =>
  import('./pages/user-create-page/user-create-page.component').then((m) => m.UserCreatePageComponent);
const notificationsListLoader = () =>
  import('./pages/notifications/notifications-list-page/notifications-list-page.component').then(
    (m) => m.NotificationsListPageComponent,
  );
const notificationSendLoader = () =>
  import('./pages/notifications/notification-send-page/notification-send-page.component').then(
    (m) => m.NotificationSendPageComponent,
  );
const formsListLoader = () =>
  import('./pages/forms/forms-list-page/forms-list-page.component').then((m) => m.FormsListPageComponent);
const formDetailLoader = () =>
  import('./pages/forms/form-detail-page/form-detail-page.component').then((m) => m.FormDetailPageComponent);
const hrFormEditorLoader = () =>
  import('./pages/forms/form-editor-page/form-editor-page.component').then((m) => m.FormEditorPageComponent);
const workflowsListLoader = () =>
  import('./pages/workflows/workflows-list-page/workflows-list-page.component').then((m) => m.WorkflowsListPageComponent);
const workflowTaskDetailLoader = () =>
  import('./pages/workflows/workflow-task-detail-page/workflow-task-detail-page.component').then((m) => m.WorkflowTaskDetailPageComponent);
const workflowTasksLoader = () =>
  import('./pages/workflows/workflow-tasks-page/workflow-tasks-page.component').then((m) => m.WorkflowTasksPageComponent);
const workflowsCreateLoader = () =>
  import('./pages/workflows/workflows-create-page/workflows-create-page.component').then((m) => m.WorkflowsCreatePageComponent);
const workflowStatusesHubLoader = () =>
  import('./pages/workflow-statuses/workflow-statuses-hub-page/workflow-statuses-hub-page.component').then(
    (m) => m.WorkflowStatusesHubPageComponent,
  );
const workflowStatusEditorLoader = () =>
  import('./pages/workflow-statuses/workflow-status-editor-page/workflow-status-editor-page.component').then(
    (m) => m.WorkflowStatusEditorPageComponent,
  );
const approverGroupsLoader = () =>
  import('./pages/approver-groups/approver-groups-page/approver-groups-page.component').then((m) => m.ApproverGroupsPageComponent);
const offboardingCasesLoader = () =>
  import('./pages/offboarding/offboarding-cases-page/offboarding-cases-page.component').then((m) => m.OffboardingCasesPageComponent);
const myClearanceTasksLoader = () =>
  import('./pages/offboarding/my-clearance-tasks-page/my-clearance-tasks-page.component').then((m) => m.MyClearanceTasksPageComponent);
const separationCaseLoader = () =>
  import('./pages/offboarding/separation-case-page/separation-case-page.component').then((m) => m.SeparationCasePageComponent);
const offboardingSettingsLoader = () =>
  import('./pages/offboarding/offboarding-settings-page/offboarding-settings-page.component').then((m) => m.OffboardingSettingsPageComponent);
const offboardingReportsLoader = () =>
  import('./pages/offboarding/offboarding-reports-page/offboarding-reports-page.component').then((m) => m.OffboardingReportsPageComponent);
const onboardingCasesLoader = () =>
  import('./pages/onboarding/onboarding-cases-page/onboarding-cases-page.component').then((m) => m.OnboardingCasesPageComponent);
const myOnboardingTasksLoader = () =>
  import('./pages/onboarding/my-onboarding-tasks-page/my-onboarding-tasks-page.component').then((m) => m.MyOnboardingTasksPageComponent);
const onboardingCaseLoader = () =>
  import('./pages/onboarding/onboarding-case-page/onboarding-case-page.component').then((m) => m.OnboardingCasePageComponent);
const onboardingSettingsLoader = () =>
  import('./pages/onboarding/onboarding-settings-page/onboarding-settings-page.component').then((m) => m.OnboardingSettingsPageComponent);
const onboardingReportsLoader = () =>
  import('./pages/onboarding/onboarding-reports-page/onboarding-reports-page.component').then((m) => m.OnboardingReportsPageComponent);
const requestDetailLoader = () =>
  import('./pages/requests/request-detail-page/request-detail-page.component').then((m) => m.RequestDetailPageComponent);
const requestCreateLoader = () =>
  import('./pages/requests/request-create-page/request-create-page.component').then((m) => m.RequestCreatePageComponent);
const projectsListLoader = () =>
  import('./pages/projects/projects-list-page/projects-list-page.component').then((m) => m.ProjectsListPageComponent);
const projectEditorLoader = () =>
  import('./pages/projects/project-editor-page/project-editor-page.component').then((m) => m.ProjectEditorPageComponent);
const projectTracksListLoader = () =>
  import('./pages/projects/project-tracks-list-page/project-tracks-list-page.component').then(
    (m) => m.ProjectTracksListPageComponent,
  );
const recruitmentFrameLoader = () =>
  import('./pages/recruitment/frame/recruitment-frame.component').then((m) => m.RecruitmentFrameComponent);
const recruitmentDashboardLoader = () =>
  import('./pages/recruitment/home/recruitment-home-page.component').then((m) => m.RecruitmentDashboardPageComponent);
const recruitmentHiringRequestsLoader = () =>
  import('./pages/recruitment/hiring-requests/recruitment-hiring-requests-page.component').then((m) => m.RecruitmentHiringRequestsPageComponent);
const recruitmentRequisitionsLoader = () =>
  import('./pages/recruitment/requisitions/recruitment-requisitions-page.component').then((m) => m.RecruitmentRequisitionsPageComponent);
const recruitmentBoardLoader = () =>
  import('./pages/recruitment/board/recruitment-board-page.component').then((m) => m.RecruitmentBoardPageComponent);
const recruitmentPipelineLoader = () =>
  import('./pages/recruitment/pipeline/recruitment-pipeline-page.component').then((m) => m.RecruitmentPipelinePageComponent);
const recruitmentCandidatesLoader = () =>
  import('./pages/recruitment/candidates/recruitment-candidates-page.component').then((m) => m.RecruitmentCandidatesPageComponent);
const recruitmentCandidateProfileLoader = () =>
  import('./pages/recruitment/candidate-profile/recruitment-candidate-profile-page.component').then((m) => m.RecruitmentCandidateProfilePageComponent);
const recruitmentApplicationsLoader = () =>
  import('./pages/recruitment/applications/recruitment-applications-page.component').then((m) => m.RecruitmentApplicationsPageComponent);
const recruitmentInboxLoader = () =>
  import('./pages/recruitment/inbox/recruitment-inbox-page.component').then((m) => m.RecruitmentInboxPageComponent);
const recruitmentInterviewsLoader = () =>
  import('./pages/recruitment/interviews/recruitment-interviews-page.component').then((m) => m.RecruitmentInterviewsPageComponent);
const recruitmentOffersLoader = () =>
  import('./pages/recruitment/offers/recruitment-offers-page.component').then((m) => m.RecruitmentOffersPageComponent);
const recruitmentSettingsLoader = () =>
  import('./pages/recruitment/settings/recruitment-settings-page.component').then((m) => m.RecruitmentSettingsPageComponent);
const reportsLibraryLoader = () => import('./pages/reports/reporting-page.component').then((m) => m.ReportsLibraryPageComponent);
const reportViewerLoader = () => import('./pages/reports/reporting-page.component').then((m) => m.ReportViewerPageComponent);
const reportBuilderLoader = () => import('./pages/reports/reporting-page.component').then((m) => m.ReportBuilderPageComponent);
const dashboardsLoader = () => import('./pages/reports/reporting-page.component').then((m) => m.DashboardsPageComponent);
const dashboardDetailLoader = () => import('./pages/reports/reporting-page.component').then((m) => m.DashboardDetailPageComponent);
const scheduledReportsLoader = () => import('./pages/reports/reporting-page.component').then((m) => m.ScheduledReportsPageComponent);

const withPendingChangesGuard = (routes: Routes): Routes =>
  routes.map((route): Route => {
    const guarded: Route = { ...route };
    if (route.component || route.loadComponent) {
      guarded.canDeactivate = [...(route.canDeactivate ?? []), pendingChangesGuard];
    }
    if (route.children) {
      guarded.children = withPendingChangesGuard(route.children);
    }
    return guarded;
  });

/**
 * Authenticated area routes (parity with WorkNest.Front.React/app).
 */
export const ADMIN_SHELL_CHILDREN: Routes = withPendingChangesGuard([
  { path: 'users/new', loadComponent: userCreateLoader },
  { path: 'users/:userId/edit', loadComponent: userEditLoader },
  { path: 'users/:userId/details', loadComponent: userDetailLoader },
  { path: 'users/:userId', loadComponent: userDetailLoader },
  { path: 'forms/new', loadComponent: hrFormEditorLoader },
  { path: 'forms/:id/edit', loadComponent: hrFormEditorLoader },
  { path: 'forms/:id', loadComponent: formDetailLoader },
  { path: 'notifications/send', loadComponent: notificationSendLoader },
  { path: 'requests/new', loadComponent: requestCreateLoader },
  { path: 'requests/:id', loadComponent: requestDetailLoader },
  { path: 'workflows/tasks/:stepId', loadComponent: workflowTaskDetailLoader },
  { path: 'workflows/tasks', loadComponent: workflowTasksLoader },
  { path: 'workflows/create', loadComponent: workflowsCreateLoader },
  { path: 'approver-groups', loadComponent: approverGroupsLoader },
  {
    path: 'workflow-statuses',
    children: [
      { path: '', pathMatch: 'full', loadComponent: workflowStatusesHubLoader },
      {
        path: 'request',
        children: [
          { path: 'new', loadComponent: workflowStatusEditorLoader, data: { workflowLutKind: 'request' } },
          { path: ':statusId/edit', loadComponent: workflowStatusEditorLoader, data: { workflowLutKind: 'request' } },
        ],
      },
      {
        path: 'task',
        children: [
          { path: 'new', loadComponent: workflowStatusEditorLoader, data: { workflowLutKind: 'task' } },
          { path: ':statusId/edit', loadComponent: workflowStatusEditorLoader, data: { workflowLutKind: 'task' } },
        ],
      },
    ],
  },
  { path: 'dashboard', loadComponent: dashboardLoader },
  { path: 'professional-profile', loadComponent: professionalProfileLoader },
  { path: 'employee-profiles', loadComponent: employeeProfilesLoader },
  { path: 'skills', loadComponent: skillsLoader },
  { path: 'employee-documents/types', loadComponent: employeeDocumentsLoader, data: { employeeDocumentsView: 'types' } },
  { path: 'employee-documents', loadComponent: employeeDocumentsLoader },
  {
    path: 'assets',
    children: [
      { path: '', pathMatch: 'full', component: AssetsPageComponent },
      { path: 'new', component: AssetEditorPageComponent },
      { path: 'categories', component: AssetsPageComponent },
      { path: 'assignments', component: AssetsPageComponent },
      { path: 'maintenance', component: AssetsPageComponent },
      { path: 'charges', component: AssetChargesPageComponent },
      { path: 'charges/:id', component: AssetChargeDetailPageComponent },
      { path: ':id/edit', component: AssetEditorPageComponent },
      { path: ':id', component: AssetDetailPageComponent },
    ],
  },
  {
    path: 'offboarding',
    // Literal sections go before ':id', or a case would be looked up by the id "settings".
    children: [
      { path: '', pathMatch: 'full', loadComponent: offboardingCasesLoader },
      { path: 'my-clearance', loadComponent: myClearanceTasksLoader },
      { path: 'settings', loadComponent: offboardingSettingsLoader },
      { path: 'reports', loadComponent: offboardingReportsLoader },
      { path: ':id', loadComponent: separationCaseLoader },
    ],
  },
  {
    path: 'onboarding',
    // Literal sections go before ':id', or a case would be looked up by the id "settings".
    children: [
      { path: '', pathMatch: 'full', loadComponent: onboardingCasesLoader },
      { path: 'my-tasks', loadComponent: myOnboardingTasksLoader },
      { path: 'settings', loadComponent: onboardingSettingsLoader },
      { path: 'reports', loadComponent: onboardingReportsLoader },
      { path: ':id', loadComponent: onboardingCaseLoader },
    ],
  },
  PERFORMANCE_ROUTE,
  TASKS_ROUTE,
  BENEFITS_ROUTE,
  BONUSES_ROUTE,
  COMMUNICATIONS_ROUTE,
  AI_AGENTS_ROUTE,
  {
    path: 'recruitment',
    // Owns the shared recruitment stylesheet (loaded once, unscoped) and renders only the outlet.
    loadComponent: recruitmentFrameLoader,
    // One instance per Recruitment visit, shared by every recruitment area; dropped when the user leaves Recruitment.
    providers: [
      RecruitmentContextStore,
      RecruitmentReferenceStore,
      RecruitmentInboxBadgeStore,
      RecruitmentBoardStateStore,
    ],
    children: [
      { path: '', pathMatch: 'full', loadComponent: recruitmentDashboardLoader },
      { path: 'hiring-requests', loadComponent: recruitmentHiringRequestsLoader },
      { path: 'requisitions', loadComponent: recruitmentRequisitionsLoader },
      // Per-job pipeline board (SP4c Task 2), opened from a requisition row; it renders its own header, not the tab bar.
      { path: 'requisitions/:jobRequisitionId/board', loadComponent: recruitmentBoardLoader },
      { path: 'pipeline', loadComponent: recruitmentPipelineLoader },
      { path: 'candidates', loadComponent: recruitmentCandidatesLoader },
      // Candidate profile (SP4c Task 3), opened from a candidate row, a board card and the board's list view; like
      // the board it renders its own header with a back link, not the area tab bar.
      { path: 'candidates/:candidateId', loadComponent: recruitmentCandidateProfileLoader },
      { path: 'applications', loadComponent: recruitmentApplicationsLoader },
      { path: 'inbox', loadComponent: recruitmentInboxLoader },
      { path: 'interviews', loadComponent: recruitmentInterviewsLoader },
      { path: 'offers', loadComponent: recruitmentOffersLoader },
      { path: 'settings', loadComponent: recruitmentSettingsLoader },
    ],
  },
  {
    path: 'reports',
    children: [
      { path: '', pathMatch: 'full', loadComponent: reportsLibraryLoader },
      { path: 'builder', loadComponent: reportBuilderLoader },
      { path: 'scheduled', loadComponent: scheduledReportsLoader },
      { path: ':id', loadComponent: reportViewerLoader },
    ],
  },
  {
    path: 'dashboards',
    children: [
      { path: '', pathMatch: 'full', loadComponent: dashboardsLoader },
      { path: ':id', loadComponent: dashboardDetailLoader },
    ],
  },
  { path: 'profile', loadComponent: profileLoader },
  { path: 'organization-settings', component: OrganizationSettingsPageComponent },
  {
    path: 'organization-identity',
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/organization-identity/organization-identity-page.component').then((m) => m.OrganizationIdentityPageComponent),
      },
      { path: 'sync-runs/:runId', loadComponent: directorySyncRunLoader },
    ],
  },
  { path: 'organization-chart', loadComponent: organizationChartLoader },
  { path: 'attendance', loadComponent: attendanceListLoader },
  { path: 'attendance/manual', loadComponent: manualAttendanceLoader },
  { path: 'field-operations', loadComponent: fieldOperationsLoader },
  {
    path: 'projects',
    // M8 (TaskProject → Project merge): one projects area. The list and editor serve attendance and Tasks; the Tasks
    // workspace (`:key/…`) needs the Tasks visit-scoped stores, provided here so the list can use them too.
    providers: [TasksContextStore, TasksReferenceStore],
    children: [
      { path: '', pathMatch: 'full', loadComponent: projectsListLoader },
      { path: 'new', loadComponent: projectEditorLoader },
      { path: 'tracks', loadComponent: projectTracksListLoader },
      { path: ':projectId/edit', loadComponent: projectEditorLoader },
      // Last: `:key` would otherwise shadow the static paths above (keys are upper case, `new`/`tracks` are not).
      PROJECT_WORKSPACE_ROUTE,
    ],
  },
  {
    path: 'branches',
    children: [
      { path: '', pathMatch: 'full', loadComponent: branchesListLoader },
      { path: 'new', loadComponent: branchEditorLoader },
      { path: ':branchId/edit', loadComponent: branchEditorLoader },
      { path: ':branchId', loadComponent: branchDetailLoader },
    ],
  },
  {
    path: 'deductions',
    children: [
      { path: '', pathMatch: 'full', loadComponent: deductionsListLoader },
      { path: 'new', loadComponent: deductionEditorLoader },
      { path: ':deductionId/edit', loadComponent: deductionEditorLoader },
      { path: ':deductionId', loadComponent: deductionDetailLoader },
    ],
  },
  {
    path: 'departments',
    children: [
      { path: '', pathMatch: 'full', loadComponent: departmentsListLoader },
      { path: 'new', loadComponent: departmentEditorLoader },
      { path: ':departmentId/edit', loadComponent: departmentEditorLoader },
      /** Parity with WorkNest.Front.React `departments/[id]/details` (read-only summary). */
      { path: ':departmentId/details', loadComponent: departmentDetailLoader },
      { path: ':departmentId', loadComponent: departmentDetailLoader },
    ],
  },
  { path: 'forms', loadComponent: formsListLoader },
  {
    path: 'holidays',
    children: [
      { path: '', pathMatch: 'full', loadComponent: holidaysListLoader },
      { path: 'new', loadComponent: holidayEditorLoader },
      { path: ':holidayId/edit', loadComponent: holidayEditorLoader },
      { path: ':holidayId', loadComponent: holidayDetailLoader },
    ],
  },
  { path: 'hourly-leave-permissions', loadComponent: hourlyLeavePermissionsLoader },
  {
    path: 'leaves',
    children: [
      { path: '', pathMatch: 'full', loadComponent: userLeaveBalancesLoader },
      { path: 'balances', pathMatch: 'full', loadComponent: userLeaveBalancesLoader },
      { path: 'balances/:userId', loadComponent: userLeaveBalanceDetailLoader },
      { path: 'new', loadComponent: leaveEditorLoader },
      {
        path: 'types',
        children: [
          { path: '', pathMatch: 'full', loadComponent: leavesListLoader },
          { path: 'new', loadComponent: leaveEditorLoader },
          { path: ':leaveId/edit', loadComponent: leaveEditorLoader },
          { path: ':leaveId', loadComponent: leaveDetailLoader },
        ],
      },
      { path: ':leaveId/edit', loadComponent: leaveEditorLoader },
      { path: ':leaveId', loadComponent: leaveDetailLoader },
    ],
  },
  {
    path: 'loan-setup',
    children: [
      { path: '', pathMatch: 'full', loadComponent: loanSetupListLoader },
      { path: 'new', loadComponent: loanSetupEditorLoader },
      { path: ':loanSetupId/edit', loadComponent: loanSetupEditorLoader },
      { path: ':loanSetupId', loadComponent: loanSetupDetailLoader },
    ],
  },
  { path: 'notifications', loadComponent: notificationsListLoader },
  {
    path: 'payroll',
    children: [
      { path: '', pathMatch: 'full', loadComponent: payrollsListLoader },
      { path: ':payrollId', loadComponent: payrollDetailLoader },
    ],
  },
  {
    path: 'policies',
    children: [
      { path: '', pathMatch: 'full', loadComponent: policiesListLoader },
      { path: 'new', loadComponent: policyEditorLoader },
      { path: 'history', loadComponent: policyHistoryLoader },
      { path: ':policyId/edit', loadComponent: policyEditorLoader },
      { path: ':policyId', loadComponent: policyDetailLoader },
    ],
  },
  {
    path: 'positions',
    children: [
      { path: '', pathMatch: 'full', loadComponent: positionsListLoader },
      { path: 'new', loadComponent: positionEditorLoader },
      { path: ':positionId/edit', loadComponent: positionEditorLoader },
      { path: ':positionId', loadComponent: positionDetailLoader },
    ],
  },
  { path: 'requests', loadComponent: requestsListLoader },
  {
    path: 'roles',
    children: [
      { path: '', pathMatch: 'full', loadComponent: rolesListLoader },
      { path: 'new', loadComponent: roleEditorLoader },
      { path: ':roleId/edit', loadComponent: roleEditorLoader },
      { path: ':roleId', loadComponent: roleDetailLoader },
    ],
  },
  {
    path: 'shifts',
    children: [
      { path: '', pathMatch: 'full', loadComponent: shiftsListLoader },
      { path: 'new', loadComponent: shiftEditorLoader },
      { path: ':shiftId/edit', loadComponent: shiftEditorLoader },
      { path: ':shiftId', loadComponent: shiftDetailLoader },
    ],
  },
  {
    path: 'substitutes',
    children: [
      { path: '', pathMatch: 'full', loadComponent: substitutesListLoader },
      { path: 'new', loadComponent: substituteEditorLoader },
      { path: ':substituteId/edit', loadComponent: substituteEditorLoader },
      { path: ':substituteId', loadComponent: substituteDetailLoader },
    ],
  },
  {
    path: 'user-budget',
    children: [
      { path: '', pathMatch: 'full', loadComponent: userBudgetListLoader },
      { path: 'new', loadComponent: userBudgetEditorLoader },
      { path: ':budgetId/edit', loadComponent: userBudgetEditorLoader },
      { path: ':budgetId', loadComponent: userBudgetDetailLoader },
    ],
  },
  { path: 'users', loadComponent: usersListLoader },
  { path: 'workflows', loadComponent: workflowsListLoader },
]);
